import { useEffect } from 'react';
import { getStoreShortId } from 'common/helpers/Store.helper';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getDefaultMenu,
  getMenuAddOns,
  getMenuCollection,
  getMenuDefaultMealBuilderPicks,
  getMenuMealBuilderPicks,
  setMenuCollection,
} from 'app/store/reducers/menu.reducer';
import { clearActiveAnnouncements, getActiveAnnouncements } from 'app/store/reducers/announcement.reducer';
import { getLastOrder, setLastOrderModel } from 'app/store/reducers/order.reducer';
import { EStoreErrorCode, EStoresLSStatus } from 'entities/Store/Store.models';

export const useMenuBootstrap = () => {
  const dispatch = useAppDispatch();

  const {
    search,
    isEnabledSearch,

    authModel,
    authModelLoading,

    customerModel,
    customerModelLoading,

    storeModel,
    storeModelLoading,
    storeModelError,
    storeLsStatus,
    storeLsStatusLoading,
  } = useAppSelector((state) => ({
    search: state.search.debouncedValue,
    isEnabledSearch: state.search.isEnabledSearch,

    authModel: state.auth.authModel,
    authModelLoading: state.auth.authModelLoading,

    customerModel: state.customer.customerModel,
    customerModelLoading: state.customer.customerModelLoading,

    storeModel: state.store.storeModel,
    storeModelLoading: state.store.storeModelLoading,
    storeModelError: state.store.storeModelError,
    storeLsStatus: state.store.storeLsStatus,
    storeLsStatusLoading: state.store.storeLsStatusLoading,
  }));

  useEffect(() => {
    const dataLoading = authModelLoading || customerModelLoading || storeModelLoading || storeLsStatusLoading;
    if (dataLoading) {
      return;
    }

    const q = isEnabledSearch ? (search ?? '').trim() : '';
    const hasSearch = q.length > 0;

    const customerStoreId = customerModel?.store?.id;
    const storeIdFromLS = storeModel?.id;
    const authorized = Boolean(authModel);

    const incorrectStoreCodeError = storeModelError?.data?.code === EStoreErrorCode.NotFound;
    const isStoreInfoUpdated = storeLsStatus === EStoresLSStatus.Updated;

    const isNonAuthUserWithoutStore = !authorized && !customerModel?.userId && !storeModel;
    const isNonAuthUserWithStore = Boolean(storeIdFromLS) && !authorized && !customerModel?.userId;
    const isAuthUserWithoutStore = authorized && customerModel?.userId && !customerStoreId;
    const isAuthUserWithStore = Boolean(customerStoreId) && authorized;

    const storeShortId = getStoreShortId();

    const needResetAuthUserStoreFromLS = (isAuthUserWithoutStore || isAuthUserWithStore) && Boolean(storeIdFromLS);
    const needResetAuthUserStoreFromCode = (isAuthUserWithoutStore || isAuthUserWithStore) && Boolean(storeShortId);

    if (incorrectStoreCodeError || needResetAuthUserStoreFromLS || needResetAuthUserStoreFromCode) {
      return;
    }

    if (!hasSearch && isEnabledSearch) {
      dispatch(setMenuCollection(null));
      dispatch(clearActiveAnnouncements());
      dispatch(setLastOrderModel(null));
      return;
    }

    if (isNonAuthUserWithoutStore || isAuthUserWithoutStore) {
      dispatch(getDefaultMenu(q));
      dispatch(getMenuDefaultMealBuilderPicks({}));
      // No store selected (default asset) → show "All stores" announcements (no storeId).
      if (!isEnabledSearch) {
        dispatch(getActiveAnnouncements());
      } else {
        dispatch(clearActiveAnnouncements());
      }
      dispatch(setLastOrderModel(null));
      return;
    }

    if (isAuthUserWithStore && customerStoreId) {
      dispatch(getMenuCollection(customerStoreId, q));
      dispatch(getMenuAddOns(customerStoreId, q));
      dispatch(getMenuMealBuilderPicks({ storeId: customerStoreId }));
      if (!isEnabledSearch) {
        dispatch(getActiveAnnouncements(customerStoreId));
        dispatch(getLastOrder({ storeId: customerStoreId }));
      }
      return;
    }

    if (isNonAuthUserWithStore && isStoreInfoUpdated && storeIdFromLS) {
      dispatch(getMenuCollection(storeIdFromLS, q));
      dispatch(getMenuAddOns(storeIdFromLS, q));
      dispatch(getMenuMealBuilderPicks({ storeId: storeIdFromLS }));
      // Guests with a selected store must see that store's announcements too.
      if (!isEnabledSearch) {
        dispatch(getActiveAnnouncements(storeIdFromLS));
      } else {
        dispatch(clearActiveAnnouncements());
      }
      dispatch(setLastOrderModel(null));
    }
  }, [
    search,
    isEnabledSearch,

    authModel,
    authModelLoading,

    customerModel,
    customerModelLoading,

    storeModel,
    storeModelLoading,
    storeModelError,
    storeLsStatus,
    storeLsStatusLoading,
  ]);
};
